<template>
	<div v-if="!isProductsEmpty" class="my-2xl h-full bg-gray-50 px-sm md:px-0">
		<v-container class="space-y-lg py-lg">
			<v-h3>{{ t("related-products") }}</v-h3>

			<v-product-recommendations
				v-slot="{isLoading, products}"
				:name="inSlider ? undefined : content?.recommendation_id"
				@no-products="isProductsEmpty = true"
			>
				<nuxt-link
					:to="url"
					class="relative grid bg-(--background-color) text-(--text-color) md:grid-cols-6 md:rounded"
					:class="{'h-full ': inWideSlider}"
					@click="addPromotionClick(promo)"
				>
				</nuxt-link>

				<div
					v-if="
						(!inSlider || !inWideSlider) && (isLoading || products.length > 0)
					"
					class="relative z-10"
				>
					<div
						class="absolute inset-x-0 -top-10 h-30 bg-linear-to-b from-transparent via-(--background-color) to-(--background-color)"
					></div>

					<div
						class="absolute inset-x-0 top-10 h-5 bg-(--background-color) md:h-[110px] md:rounded-b"
					></div>

					<v-scrollable
						class="flex snap-x scroll-px-sm flex-nowrap space-x-xs px-sm md:scroll-px-lg md:space-x-sm md:px-lg"
					>
						<template v-if="isLoading">
							<v-scrollable-item
								v-for="i in 10"
								:key="i"
								class="mb-auto w-40 shrink-0 snap-start last:mr-lg md:w-65"
							>
								<v-product-card-placeholder />
							</v-scrollable-item>
						</template>

						<v-scrollable-item
							v-for="product in products"
							v-else
							:key="product.key"
							class="w-40 shrink-0 snap-start md:w-65"
						>
							<v-elevate-listing-product-card :product="product" />
						</v-scrollable-item>
					</v-scrollable>
				</div>
			</v-product-recommendations>
		</v-container>
	</div>
</template>

<script setup lang="ts">
import type {ISbStoryData} from "storyblok-js-client";
import {type ProductRecommendationsContent} from "@pkg/shared/types";

import {usePromotion} from "@pkg/shared/composables/use-promotion";

const properties = defineProps<{
	story: ISbStoryData;
	content?: ProductRecommendationsContent;
}>();

const {addPromotionClick} = usePromotion();

/**
 * Problem: We need to figure out how to use article page path for fetching product
 * recommendations from Elevate. At current state we can't use the path now as is if we don't
 * corresponding path set in Elevate for article page.
 */

const blokData = computed(() => properties.story.content);

const inWideSlider = inject("sb.inWideSlider", false);
const inSlider = inject("sb.inSlider", false);

const url = computed(() => `/${blokData.value.title}`);

function convertString(string: string) {
	const words = string.split("-");
	const convertedString = words
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
		.join(" ");
	return convertedString;
}

const {t} = useI18n();

const route = useRoute();

const promo = computed(() => ({
	creative_name: convertString(blokData?.value?.component ?? "") ?? "Promotion",
	creative_slot: `${blokData.value.component} / 0`, // component name / index

	promotion_id: blokData.value._uid ?? "",
	promotion_name: blokData.value.title,
	source_url: route.fullPath,
	target_url: url.value,
}));

const isProductsEmpty = ref<boolean>(false);
</script>

<style scoped></style>

<i18n lang="json">
{
	"et": {
		"related-products": "Postitusega seotud tooted"
	},
	"fi": {
		"related-products": "Artikkeliin liittyviä tuotteita"
	}
}
</i18n>
